import { useWidget } from "../../../providers/widget"
import { ReviewsWidgetConfig } from "../config"
import { ReviewsCarousel } from './reviews-carousel'
import { ReviewsGrid } from './reviews-grid'
import { ReviewsBadge } from './reviews-badge'
import { ReviewsList } from './reviews-list'
import { ReviewsHeader } from './partials/reviews-header'
import { AllReviewsSection } from './partials/all-reviews-section'
import { ReviewDetailsModal } from './partials/review-details-modal'
import { ReviewsProvider, useReviewsConfig } from '../providers'
import { useFetchReviews } from '../data'
import Stylesheet from "../../../components/Stylesheet"
import WebFont from "../../../components/WebFont"
import WidgetSection from "../../../components/WidgetSection"
import WidgetDataError from "../../../components/WidgetDataError"
import { ReviewsSearch } from './partials/reviews-search'
import { filterReviews } from '../helpers/utils'
import { useReviewsCss } from "../hooks/useReviewsCss"
import { resolveWidgetSpacingStyle } from "@homerunner-next/widget-core/spacing"

function ReviewsAppContent() {
  const widgetCtx = useWidget<ReviewsWidgetConfig>()
  const options = useReviewsConfig()
  const { data, isLoading, isError, error, fetchNextPage } = useFetchReviews(options.filter)
  const cssString = useReviewsCss()

  const allReviews = data?.pages?.[0]?.result || []
  const reviews = filterReviews(allReviews, options.searchQuery)
  const searchActive = Boolean(options.showSearch) && options.layout !== 'badge'
  const noMatches = searchActive && options.searchQuery.trim() !== '' && reviews.length === 0

  if (isLoading) return null
  if (isError) return <WidgetDataError error={error} />
  if (!allReviews.length) return null

  // #1402 — a floating badge (`badge.type === 'floating'`, the ReviewsBadge
  // gate that adds its own `fixed` class) renders position: fixed, so this
  // container collapses to zero in-flow height. Marking it here, in both the
  // SSR (renderToString) and CSR output, is the "light-DOM SSR equivalent"
  // the issue asks for: property-details-layout keys its own zero-margin
  // rule on this same data-hr-inflow="none" attribute rather than on the
  // "reviews" widget type, since list/grid/carousel (and a static badge)
  // stay in flow and must keep the ordinary row margin.
  const isZeroFootprint = options.layout === 'badge' && options.badge?.type === 'floating'

  return (
    <div
      className={`homerunner-reviews-container w-full ${widgetCtx.resolvedTheme === 'dark' ? 'dark' : ''}`}
      style={resolveWidgetSpacingStyle(widgetCtx.options)}
      data-hr-inflow={isZeroFootprint ? 'none' : undefined}
      suppressHydrationWarning
    >
      <WebFont />
      <Stylesheet css={cssString} />
      <WidgetSection show={options.section?.show} title={options.section?.title} />

      {options.header && options.header.show !== false && options.layout !== "badge" && (
        <div className="homerunner-reviews-header-wrapper mb-8">
          <ReviewsHeader
            headerTitle={options.header.title}
            buttonText={options.header.buttonText}
            buttonHref={options.header.buttonHref}
            averageRating={data?.pages?.[0]?.pagination?.average_rating}
            reviewCount={data?.pages?.[0]?.pagination?.total_items}
            loading={isLoading}
            hideOverallRating={options.hideOverallRating}
          />
        </div>
      )}

      {searchActive && <ReviewsSearch />}
      {noMatches && (
        <p className="homerunner-reviews-search-empty text-muted-foreground py-6">No reviews match your search.</p>
      )}

      {options.layout === 'carousel' && !noMatches && (
        <div className="homerunner-reviews-carousel-wrapper">
          <ReviewsCarousel reviews={reviews} />
        </div>
      )}
      {options.layout === 'grid' && !noMatches && (
        <div className="homerunner-reviews-grid-wrapper">
          <ReviewsGrid
            reviews={reviews}
            hasNextPage={undefined}
            isFetchingNextPage={undefined}
            onLoadMore={() => fetchNextPage()}
          />
        </div>
      )}
      {options.layout === 'badge' && (
        <div className="homerunner-reviews-badge-wrapper">
          <ReviewsBadge />
        </div>
      )}
      {options.layout === 'list' && !noMatches && (
        <div className="homerunner-reviews-list-wrapper">
          <ReviewsList />
        </div>
      )}

      {options.layout !== 'badge' && options.layout !== 'list' && <AllReviewsSection />}

      {options.popupForDetail && (
        <div className="homerunner-reviews-modal-wrapper">
          <ReviewDetailsModal />
        </div>
      )}
    </div>
  )
}

export function ReviewsWidgetContent() {
  const { options, resolvedTheme } = useWidget<ReviewsWidgetConfig>()

  return (
    <ReviewsProvider options={options} resolvedTheme={resolvedTheme}>
      <ReviewsAppContent />
    </ReviewsProvider>
  )
}
